import { useMemo } from 'react';

// Utils
import cn from '../../../../utils/cn';
import { getContrastTextColor } from '../../Fields/ColorField/useColorField';

// Styles
import styles from './SimpleColorCell.module.css';

/**
 * Simple color cell component for SimpleTable
 * @param {object} props
 * @param {string} [props.value] - Color value in hex format (e.g., "#FF0000")
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.placeholder='-'] - Placeholder text when empty
 * @param {'left' | 'center' | 'right'} [props.align='center'] - Text alignment
 * @param {boolean} [props.bold=false] - Text in bold
 * @param {boolean} [props.italic=false] - Text in italic
 * @returns {JSX.Element}
 */
function SimpleColorCell({
  value,
  className = '',
  placeholder = '-',
  align = 'center',
  bold = false,
  italic = false,
}) {
  const isEmpty = !value || value === '';

  const textColor = useMemo(
    () => (isEmpty ? 'inherit' : getContrastTextColor(value)),
    [value, isEmpty],
  );

  const backgroundColor = useMemo(
    () => (isEmpty ? 'transparent' : value),
    [value, isEmpty],
  );

  const cellClassName = cn([
    styles['simple-color-cell'],
    className,
    bold && styles['simple-color-cell-bold'],
    italic && styles['simple-color-cell-italic'],
    align && styles[`simple-color-cell-align-${align}`],
  ]);

  if (isEmpty) {
    return (
      <div className={cellClassName}>
        <div className={styles['simple-color-cell-display']}>
          {placeholder}
        </div>
      </div>
    );
  }

  return (
    <div className={cellClassName}>
      <div
        className={styles['simple-color-cell-display']}
        style={{
          backgroundColor,
          color: textColor,
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default SimpleColorCell;
